import { transition, trigger, useAnimation } from '@angular/animations';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';

import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  NgControl,
  UntypedFormControl,
  ValidatorFn,
} from '@angular/forms';
import { bounceIn } from 'ng-animate';
import { distinctUntilChanged } from 'rxjs/operators';

import { EnableClassTelemetry } from 'app/services/otel.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-lex-input',
  templateUrl: './lex-input.component.html',
  styleUrls: ['./lex-input.component.scss'],
  animations: [trigger('suffixIconIn', [transition(':enter', useAnimation(bounceIn))])],
  standalone: false,
})
export class LexInputComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() placeholder: string;
  @Input() name = '';
  @Input() inputClass = '';
  @Input() disabled: boolean;
  @Input() required: boolean;
  @Input() readonly: boolean;
  @Input() value: string;
  @Input() suffixIcon: string;
  @Input() suffixAction: ((val) => void) | 'clear';
  @Input() validators: ValidatorFn[];
  @Input() asyncValidators: AsyncValidatorFn[];
  @Input() validatorMessages: { [key: string]: string };
  @Input() autofocus: boolean;

  @Output() valueChange = new EventEmitter<string>();
  @Output() focus = new EventEmitter<any>();

  control: AbstractControl = new UntypedFormControl('');

  private originalSuffixIcon;

  constructor(@Self() @Optional() public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    if (!this.name) {
      this.name = this.uuidv4();
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.control = this.ngControl.control;
    }

    if (this.suffixAction === 'clear') {
      this.suffixIcon = this.suffixIcon || 'close';
    }

    this.control.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => {
      if (this.suffixAction === 'clear') {
        if (val) {
          if (this.suffixIcon !== 'close') {
            this.originalSuffixIcon = this.suffixIcon;
            this.suffixIcon = 'close';
          }
        } else if (this.originalSuffixIcon) {
          this.suffixIcon = this.originalSuffixIcon;
        }
      }
      this.valueChange.emit(val);
    });
  }

  ngOnChanges(changes) {
    if (changes.disabled) {
      this.updateDisabled();
    }

    if (changes.validators) {
      this.control.setValidators(this.validators);
      this.control.updateValueAndValidity();
    }

    if (changes.asyncValidators) {
      this.control.setAsyncValidators(this.asyncValidators);
      this.control.updateValueAndValidity();
    }

    if (changes.value && changes.value.currentValue !== this.control.value) {
      this.control.setValue(this.value || '');
      this.control.updateValueAndValidity();
      this.updateDisabled();
    }
  }

  updateDisabled() {
    if (this.disabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  onSuffixActionClicked() {
    if (this.suffixAction === 'clear') {
      this.control.setValue('');
      this.control.updateValueAndValidity();
    } else if (typeof this.suffixAction === 'function') {
      this.suffixAction(this.control.value);
    }
  }

  /* eslint-disable no-bitwise */
  uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  /* eslint-enable no-bitwise */

  get errorMessage() {
    if (this.control.invalid && this.validatorMessages) {
      return this.validatorMessages[Object.keys(this.control.errors)[0]];
    }
  }

  //ControlValueAccessor interface
  writeValue(_obj: any) {}

  registerOnChange(_fn: any) {}

  registerOnTouched(_fn: any) {}

  setDisabledState?(_isDisabled: boolean) {}
}
